import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { Text, Button } from '@ebh/capsule';
import TextInput from '../../components/text-input';
import {
  ArrowBackwardIcon,
  VisibilityIcon,
  VisibilityOffIcon,
} from '@ebh/capsule/icons';
import { useChangePassword } from '../../api/hooks/settings/use-change-password';
import cookiesManager from '../../utils/cookies';
import { AUTH_KEYS } from '../../utils/cookies/keys';
import styles from './styles';
import PasswordRules from '../../components/password-rules';
import {
  PASSWORD_RULES,
  getConfirmPasswordRules,
} from '../../utils/validations/password';

type FormValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

interface CreateNewPasswordProps {
  onClose: () => void;
  successAlert: (msg: string) => void;
  errorAlert: (msg: string) => void;
}

export const CreateNewPassword = ({
  onClose,
  successAlert,
  errorAlert,
}: CreateNewPasswordProps) => {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const newPassword = watch('newPassword');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { changePwd, isChangePasswordPending } = useChangePassword({
    onSuccess: (data) => {
      cookiesManager.setMultiple([
        { key: AUTH_KEYS.ACCESS_TOKEN, value: data.data?.accessToken ?? '' },
        { key: AUTH_KEYS.REFRESH_TOKEN, value: data.data?.refreshToken ?? '' },
        { key: AUTH_KEYS.EXPIRY_TIME, value: data.data?.expiryTime ?? '' },
      ]);

      reset();
      successAlert('Password changed successfully');
      setTimeout(() => {
        onClose();
      }, 2000);
    },

    onError: (error) => {
      errorAlert(error?.message || 'Password not changed');
    },
  });

  const renderEyeIcon = (show: boolean, toggle: () => void) => (
    <div
      css={styles.eyeIcon}
      onClick={toggle}
      onMouseDown={(e) => e.preventDefault()}
    >
      {show ? (
        <VisibilityIcon color="gray-500" />
      ) : (
        <VisibilityOffIcon color="gray-500" />
      )}
    </div>
  );

  const handleFormSubmit = (data: FormValues) => {
    changePwd({
      oldPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  };

  return (
    <div css={styles.root}>
      <div css={styles.header}>
        <ArrowBackwardIcon
          color="gray-500"
          onClick={onClose}
          css={[{ cursor: 'pointer' }]}
        />
        <Text color="gray-500" variant="header-2">
          Update Details
        </Text>
      </div>

      <form css={styles.form} onSubmit={handleSubmit(handleFormSubmit)}>
        <div css={styles.container}>
          <div css={styles.miniContainer}>
            <div css={styles.inputGroup}>
              <TextInput
                control={control}
                name="currentPassword"
                label="Current Password"
                placeholder="Enter Current Password"
                type={showCurrent ? 'text' : 'password'}
                end={renderEyeIcon(showCurrent, () =>
                  setShowCurrent(!showCurrent),
                )}
              />
              <TextInput
                control={control}
                name="newPassword"
                label="New Password"
                placeholder="Enter New Password"
                type={showNew ? 'text' : 'password'}
                rules={PASSWORD_RULES}
                end={renderEyeIcon(showNew, () => setShowNew(!showNew))}
                error={!!errors.newPassword}
                helperText=""
              />

              <TextInput
                control={control}
                name="confirmPassword"
                label="Confirm New Password"
                placeholder="Confirm New Password"
                type={showConfirm ? 'text' : 'password'}
                rules={getConfirmPasswordRules(() => newPassword)}
                end={renderEyeIcon(showConfirm, () =>
                  setShowConfirm(!showConfirm),
                )}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
              />
            </div>

            <PasswordRules password={newPassword || ''} />
          </div>
        </div>

        <div css={styles.buttonContainer}>
          <Button
            type="submit"
            css={styles.submitButton}
            variant="primary"
            disabled={!isValid || isChangePasswordPending}
            loading={isChangePasswordPending}
          >
            Update
          </Button>
        </div>
      </form>
    </div>
  );
};
